import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getSidebarSectionFromPath, SidebarSection } from '../sidebarUtils'
import { defaultOpenSections, OPEN_SECTIONS_STORAGE_KEY } from '../sidebarConstants'

const getInitialOpenSections = (): Record<SidebarSection, boolean> => {
  const stored = localStorage.getItem(OPEN_SECTIONS_STORAGE_KEY)
  if (!stored) {
    return defaultOpenSections
  }

  try {
    return { ...defaultOpenSections, ...JSON.parse(stored) }
  } catch {
    return defaultOpenSections
  }
}

export const useSidebarSections = () => {
  const location = useLocation()
  const [openSections, setOpenSections] = useState<Record<SidebarSection, boolean>>(getInitialOpenSections)

  const persistSections = (sections: Record<SidebarSection, boolean>) => {
    localStorage.setItem(OPEN_SECTIONS_STORAGE_KEY, JSON.stringify(sections))
    return sections
  }

  const toggleSection = useCallback((section: SidebarSection) => {
    setOpenSections(prev => persistSections({ ...prev, [section]: !prev[section] }))
  }, [])

  const setSectionOpen = useCallback((section: SidebarSection, value: boolean) => {
    setOpenSections(prev => {
      if (prev[section] === value) return prev
      return persistSections({ ...prev, [section]: value })
    })
  }, [])

  useEffect(() => {
    const section = getSidebarSectionFromPath(location.pathname)
    if (section) {
      setSectionOpen(section, true)
    }
  }, [location, setSectionOpen])

  return { openSections, toggleSection, setSectionOpen }
}
